import api from "API";
import * as Params from "API/params";

import {
  GetIntegrationSubmissionSlots,
  GetIntegrationsByReportingYear,
  GetManualSubmissionCadenceDetails,
  GetManualSubmissionFiles,
  GetManualSubmissionOverview,
  GetManualSubmissionReminderEmailAddresses,
  GetManualSubmissionSlots,
  IntegrationByReportingYear,
} from "./payloads";

export const ManualSubmissionEndpoint =
  "/organisations/:org/manual-submissions/";
export const AutomatedCollectionEndpoint = "/organisations/:org/integrations/";
export const IntegrationSubmissions = "/organisations/:org/";

const extendedEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    manualSubmissionOverview: builder.query<
      GetManualSubmissionOverview,
      Params.GenericParamMap
    >({
      query: (query) => ({
        url: ManualSubmissionEndpoint + "overview",
        method: "GET",
        params: {
          reporting_year: query.reporting_year,
        },
      }),
    }),
    manualSubmissionSubmissionSlots: builder.query<
      GetManualSubmissionSlots,
      Params.GenericParamMap
    >({
      query: (query) => {
        const params = {
          page: query.page,
          page_size: query.page_size,
          ordering: Params.toString(query.ordering),
          reporting_year: query.reporting_year,
          entity: Params.toArray(query.entity),
          activity: Params.toArray(query.activity),
          status: Params.toArray(query.status),
          time_period: Params.toArray(query.submissionPeriod),
          cadence: Params.toString(query.cadence),
          submission_group: Params.toArray(query.submissionGroup),
          has_draft: query.has_draft,
        };

        return {
          url: ManualSubmissionEndpoint,
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
        };
      },
    }),
    manualSubmissionFiles: builder.query<
      GetManualSubmissionFiles,
      Params.GenericParamMap
    >({
      query: (query) => {
        const params = {
          page: query.page,
          page_size: query.page_size,
          ordering: Params.toString(query.ordering),
          reporting_year: query.reporting_year,
          entities: Params.toArray(query.entity),
          activities: Params.toArray(query.activity),
          status: Params.toArray(query.status),
          time_period: Params.toArray(query.submissionPeriod),
          submission_group: Params.toArray(query.submissionGroup),
          submitted_on: Params.toString(query.submitted_on),
          cadence: Params.toArray(query.cadence),
        };

        return {
          url: ManualSubmissionEndpoint + "files",
          params,
        };
      },
    }),
    manualSubmissionCadenceDetails: builder.query<
      GetManualSubmissionCadenceDetails,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url:
            ManualSubmissionEndpoint +
            `reporting-years/${query.reporting_year}/cadence-details/${query.cadence}`,
        };
      },
    }),
    manualSubmissionReminderEmailAddresses: builder.query<
      GetManualSubmissionReminderEmailAddresses,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url: ManualSubmissionEndpoint + "reminder-email-addresses",
          method: "GET",
          params: {
            page: query.page,
            page_size: query.page_size,
            reporting_year: query.reporting_year,
            entities: Params.toArray(query.entity),
            activities: Params.toArray(query.activity),
            status: Params.toArray(query.status),
            time_period: Params.toArray(query.submissionPeriod),
            cadence: Params.toString(query.cadence),
            id_in: Params.toList(query.id_in),
          },
        };
      },
    }),
    manualSubmissionSendEmailReminders: builder.mutation<
      GetManualSubmissionReminderEmailAddresses,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url: ManualSubmissionEndpoint + "send-email-reminders",
          method: "POST",
          params: {
            page: query.page,
            page_size: query.page_size,
            reporting_year: query.reporting_year,
            entities: Params.toArray(query.entity),
            activities: Params.toArray(query.activity),
            status: Params.toArray(query.status),
            time_period: Params.toArray(query.submissionPeriod),
            cadence: Params.toString(query.cadence),
            id_in: Params.toList(query.id_in),
          },
        };
      },
    }),
    manualSubmissionExportToExcel: builder.query<Blob, Params.GenericParamMap>({
      query: (query) => {
        const params = {
          page: query.page,
          page_size: query.page_size,
          reporting_year: query.reporting_year,
          entity: Params.toArray(query.entity),
          activity: Params.toArray(query.activity),
          status: Params.toArray(query.status),
          time_period: Params.toArray(query.submissionPeriod),
          id_in: Params.toList(query.id_in),
        };

        return {
          url: ManualSubmissionEndpoint + "export",
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    integrationsByReportingYear: builder.query<
      GetIntegrationsByReportingYear,
      Params.GenericParamMap
    >({
      query: (query) => {
        return {
          url: AutomatedCollectionEndpoint,
          method: "GET",
          params: {
            reporting_year: query.reportingYearId,
          },
        };
      },
    }),
    integrationDetails: builder.query<
      IntegrationByReportingYear,
      Params.GenericParamMap
    >({
      query: ({ dataCollectionMethodId }) => {
        return {
          url: `${AutomatedCollectionEndpoint}${dataCollectionMethodId}`,
          method: "GET",
        };
      },
    }),
    integrationSubmissionGroupSlots: builder.query<
      GetIntegrationSubmissionSlots,
      Params.GenericParamMap
    >({
      query: ({ groupId, time_period, status, page }) => {
        return {
          url: `${IntegrationSubmissions}submission-groups/${groupId}/integration-submission-slots`,
          method: "GET",
          params: {
            time_period: Params.toArray(time_period),
            status: Params.toArray(status),
            page_size: 60,
            page,
          },
        };
      },
    }),
  }),
});

export const {
  useManualSubmissionOverviewQuery,
  useManualSubmissionSubmissionSlotsQuery,
  useManualSubmissionFilesQuery,
  useManualSubmissionCadenceDetailsQuery,
  useLazyManualSubmissionReminderEmailAddressesQuery,
  useManualSubmissionSendEmailRemindersMutation,
  useLazyManualSubmissionExportToExcelQuery,
  useIntegrationsByReportingYearQuery,
  useLazyIntegrationsByReportingYearQuery,
  useIntegrationDetailsQuery,
  useIntegrationSubmissionGroupSlotsQuery,
} = extendedEndpoints;
